
import { 
  collection, 
  doc,
  addDoc, 
  updateDoc, 
  serverTimestamp,
  writeBatch,
  FirestoreError
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Lesson } from '@/models/Lesson';
import { toast } from '@/lib/toast';
import { FirestoreService } from '../base/FirestoreService';
import { profileService } from '@/services';

export class LessonMutationService extends FirestoreService {
  constructor() {
    super('lessons');
  }

  // Add a new lesson (admin only)
  async addLesson(userId: string, lesson: Omit<Lesson, 'id'>): Promise<Lesson | null> {
    try {
      if (!userId) {
        console.error('Error adding lesson: No user ID provided');
        toast.error('Authentication required');
        return null;
      }

      // Get a reference to the collection
      const colRef = collection(db, this.collectionName);
      
      // Get the highest order number from all lessons
      const lessonQueryService = new LessonQueryService();
      const existingLessons = await lessonQueryService.getAllLessons();
      const maxOrder = existingLessons.reduce((max, lesson) => 
        (lesson.order !== undefined && lesson.order > max) ? lesson.order : max, -1);
      
      // Prepare the lesson data with timestamps and user ID
      const lessonData = {
        ...lesson,
        userId,
        active: lesson.active !== undefined ? lesson.active : true,
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Adding lesson with data:', { ...lessonData, userId });
      
      const docRef = await addDoc(colRef, lessonData);
      console.log('Lesson added successfully with ID:', docRef.id);
      
      return {
        id: docRef.id,
        name: lesson.name,
        words: lesson.words,
        active: lessonData.active,
        order: lessonData.order,
        createdAt: lessonData.createdAt,
        updatedAt: lessonData.updatedAt,
        userId
      };
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error adding lesson:', firestoreError);
      toast.error(`Failed to add lesson: ${firestoreError.message}`);
      return null;
    }
  }

  // Update a lesson (admin only)
  async updateLesson(lessonId: string, updates: Partial<Lesson>): Promise<boolean> {
    return this.executeUpdate(lessonId, updates, 'Failed to update lesson');
  }

  // Delete a lesson (admin only)
  async deleteLesson(lessonId: string): Promise<boolean> {
    return this.executeDelete(lessonId, 'Failed to delete lesson');
  }

  // Update the order of multiple lessons (admin only)
  async updateLessonsOrder(lessons: Lesson[]): Promise<boolean> {
    try {
      const batch = writeBatch(db);
      
      lessons.forEach((lesson, index) => {
        const lessonRef = doc(db, this.collectionName, lesson.id);
        batch.update(lessonRef, { 
          order: index,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error updating lessons order:', firestoreError);
      toast.error(`Failed to update lessons order: ${firestoreError.message}`);
      return false;
    }
  }
  
  // Method to mark lesson as completed for a user
  async markLessonCompleted(lessonId: string, userId: string): Promise<boolean> {
    try {
      console.debug('📝 Marking lesson as completed:', lessonId, 'for user:', userId);
      
      // Instead of updating the lesson document, we'll record the completion in the user's profile
      const success = await profileService.recordLessonCompletion(userId, lessonId);
      
      if (!success) {
        console.error('❌ Failed to record lesson completion in profile');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error marking lesson as completed:', error);
      return false;
    }
  }
}

// Import for the LessonQueryService
import { LessonQueryService } from './LessonQueryService';
